export type TimeframeDatapoint = {
  start: Date;
  center?: Date;
  end?: Date;
};

export type NumericalDatapoint = {
  start: number;
};

/**
 * Gets if a datapoint is connected to a gap
 *
 * @param datapoint - given datapoint
 * @param nextDatapoint - following datapoint
 * @param threshold - given GapPolicy threshold
 * @returns
 */
export function getDatapointIsGap<
  T extends TimeframeDatapoint | NumericalDatapoint,
>(datapoint: T, nextDatapoint: T, threshold: number = 0): boolean {
  return hasGap(datapoint, nextDatapoint, threshold);
}

/**
 * Detects a gap between current and next data point.
 *
 * The distance between data points is measured, then we compare it to the given threshold.
 * When the distance is bigger than the threshold, we consider it a gap.
 *
 * It treats the timestamps as timeframes setting the end date as the start date.
 *
 * @param currentDatapoint - given current TimeframeDatapoint
 * @param nextDatapoint - given next TimeframeDatapoint
 * @param threshold - threshold to detect gaps
 * @returns
 */
function hasGap<T extends TimeframeDatapoint | NumericalDatapoint>(
  currentDatapoint: T,
  nextDatapoint: T | undefined,
  threshold: number,
): boolean {
  if (!currentDatapoint || !nextDatapoint) {
    return false;
  }

  const distance = Math.abs(getDistance(nextDatapoint, currentDatapoint));

  const hasGap = distance > 0;
  const isGapOutsideThreshold = threshold < distance;

  return hasGap && isGapOutsideThreshold;
}

function isNumericalDatapoint(
  datapoint: TimeframeDatapoint | NumericalDatapoint,
): datapoint is NumericalDatapoint {
  return typeof datapoint.start === 'number';
}

function isTimeframeDatapoint(
  datapoint: TimeframeDatapoint | NumericalDatapoint,
): datapoint is TimeframeDatapoint {
  return datapoint.start instanceof Date;
}

/**
 * Gets the distance between two data points.
 *
 * @param nextDatapoint - given first data point
 * @param currentDatapoint - given second data point
 * @returns
 */
function getDistance<T extends TimeframeDatapoint | NumericalDatapoint>(
  nextDatapoint: T,
  currentDatapoint: T,
): number {
  if (
    isNumericalDatapoint(nextDatapoint) &&
    isNumericalDatapoint(currentDatapoint)
  ) {
    return nextDatapoint.start - currentDatapoint.start;
  }
  if (
    isTimeframeDatapoint(nextDatapoint) &&
    isTimeframeDatapoint(currentDatapoint)
  ) {
    return (
      nextDatapoint.start.getTime() - resolveEndDate(currentDatapoint).getTime()
    );
  }
  return 0;
}

/**
 * Resolves the end Date of a given data point. When the end is undefined, it fallbacks to start.
 *
 * @param datapoint - given TimeseriesDatapoint
 * @returns
 */
function resolveEndDate(datapoint: TimeframeDatapoint): Date {
  return datapoint.end ?? datapoint.start;
}
